import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../sessionSlice";
import { fetchThemeData } from "../../themeSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { clearAdminDirty, setAdminDirty } from "../../adminDirty";
import { FONT_FAMILY_OPTIONS, ensureGoogleFont } from "../../components/TextStyleControls";
import "./mosaicSettings.css";

const MOSAIC_WORD_MAX = 20;
const MOSAIC_TITLE_MAX = 100;
const DEFAULT_WORD = "India";
const DEFAULT_TITLE = "Photo mosaic";
const DEFAULT_DESKTOP = "16rem";
const DEFAULT_MOBILE = "4rem";
const WORD_DESKTOP_MAX_REM = 20;
const WORD_MOBILE_MAX_REM = 5;
const WORD_MIN_REM = 0.5;

const TITLE_DESKTOP_MIN = 0.75;
const TITLE_DESKTOP_MAX = 4;
const TITLE_MOBILE_MIN = 0.75;
const TITLE_MOBILE_MAX = 3;
const DEFAULT_TITLE_DESKTOP = "1.5rem";
const DEFAULT_TITLE_MOBILE = "1.125rem";
const DEFAULT_TITLE_COLOR = "#111111";

const PARTIAL_REM_RE = /^\d*\.?\d*$/;

/** Parse mosaic word font size into rem units (px → rem). */
function parseWordFontRem(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s) return null;
  const m = s.match(/^(\d+(?:\.\d+)?)(px|rem|em)?$/);
  if (!m) return null;
  const n = Number.parseFloat(m[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = m[2] || "rem";
  if (unit === "px") return n / 16;
  return n;
}

function formatWordFontRem(rem) {
  const rounded = Math.round(rem * 1000) / 1000;
  return `${rounded}rem`;
}

/**
 * Validate + clamp mosaic word font size.
 * Returns { ok, value, error } where value is normalized (e.g. "16rem").
 */
function validateWordFontSize(raw, { fallback, minRem, maxRem, label }) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) {
    return {
      ok: false,
      value: fallback,
      error: `${label} cannot be blank.`,
    };
  }
  const rem = parseWordFontRem(trimmed);
  if (rem == null) {
    return {
      ok: false,
      value: fallback,
      error: `${label} must be a positive number with px, rem, or em (e.g. ${fallback}).`,
    };
  }
  if (rem > maxRem) {
    return {
      ok: false,
      value: formatWordFontRem(maxRem),
      error: `${label} cannot exceed ${maxRem}rem.`,
      exceeded: true,
    };
  }
  if (rem < minRem) {
    return {
      ok: false,
      value: formatWordFontRem(minRem),
      error: `${label} must be at least ${minRem}rem.`,
    };
  }
  // Prefer rem output for consistency
  return { ok: true, value: formatWordFontRem(rem), error: "" };
}

/** Convert stored title size (rem string, bare number, or legacy px) → "1.5rem". */
function toRemString(raw, fallback, min, max) {
  const fallbackRem = String(fallback).endsWith("rem") ? fallback : `${fallback}rem`;
  if (raw == null || String(raw).trim() === "") return fallbackRem;

  const s = String(raw).trim().toLowerCase();
  let num;
  if (/^\d+(\.\d+)?rem$/.test(s)) {
    num = Number.parseFloat(s);
  } else if (/^\d+(\.\d+)?px$/.test(s)) {
    num = Number.parseFloat(s) / 16;
  } else if (/^\d+(\.\d+)?em$/.test(s)) {
    num = Number.parseFloat(s);
  } else if (/^\d+(\.\d+)?$/.test(s)) {
    const n = Number.parseFloat(s);
    // Legacy px integers from TextStyleControls (e.g. 24 → 1.5rem)
    num = n > max ? n / 16 : n;
  } else {
    return fallbackRem;
  }

  if (!Number.isFinite(num) || num <= 0) return fallbackRem;
  const clamped = Math.max(min, Math.min(max, num));
  const rounded = Math.round(clamped * 1000) / 1000;
  return `${rounded}rem`;
}

function parseTitleStyle(raw) {
  let parsed = raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {
      font_family: "",
      font_size_desktop: DEFAULT_TITLE_DESKTOP,
      font_size_mobile: DEFAULT_TITLE_MOBILE,
      color: DEFAULT_TITLE_COLOR,
    };
  }
  return {
    font_family: parsed.font_family != null ? String(parsed.font_family) : "",
    font_size_desktop: toRemString(
      parsed.font_size_desktop,
      DEFAULT_TITLE_DESKTOP,
      TITLE_DESKTOP_MIN,
      TITLE_DESKTOP_MAX
    ),
    font_size_mobile: toRemString(
      parsed.font_size_mobile,
      DEFAULT_TITLE_MOBILE,
      TITLE_MOBILE_MIN,
      TITLE_MOBILE_MAX
    ),
    color: parsed.color || parsed.text_color || DEFAULT_TITLE_COLOR,
  };
}

function remPreviewPx(remStr, cap = 22) {
  const n = Number.parseFloat(String(remStr));
  if (!Number.isFinite(n)) return 16;
  return Math.min(Math.max(n * 16, 10), cap);
}

export default function MosaicSettings() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((s) => s.theme);

  const [word, setWord] = useState(DEFAULT_WORD);
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [titleFamily, setTitleFamily] = useState("");
  const [titleDesktop, setTitleDesktop] = useState(DEFAULT_TITLE_DESKTOP);
  const [titleMobile, setTitleMobile] = useState(DEFAULT_TITLE_MOBILE);
  const [titleColor, setTitleColor] = useState(DEFAULT_TITLE_COLOR);
  const [titleDesktopError, setTitleDesktopError] = useState("");
  const [titleMobileError, setTitleMobileError] = useState("");
  const [desktopFont, setDesktopFont] = useState(DEFAULT_DESKTOP);
  const [mobileFont, setMobileFont] = useState(DEFAULT_MOBILE);
  const [desktopFontError, setDesktopFontError] = useState("");
  const [mobileFontError, setMobileFontError] = useState("");
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const markDirty = useCallback(() => {
    if (!hydrated) return;
    setAdminDirty(true, "Mosaic");
  }, [hydrated]);

  useEffect(() => {
    return () => clearAdminDirty();
  }, []);

  useEffect(() => {
    setHydrated(false);
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    if ("mosaic_word" in data) {
      setWord(String(data.mosaic_word ?? DEFAULT_WORD).slice(0, MOSAIC_WORD_MAX));
    }
    if ("mosaic_title" in data) {
      setTitle(String(data.mosaic_title ?? DEFAULT_TITLE).slice(0, MOSAIC_TITLE_MAX));
    }
    if ("mosaic_title_style" in data) {
      const next = parseTitleStyle(data.mosaic_title_style);
      setTitleFamily(next.font_family);
      setTitleDesktop(next.font_size_desktop);
      setTitleMobile(next.font_size_mobile);
      setTitleColor(next.color);
      setTitleDesktopError("");
      setTitleMobileError("");
      if (next.font_family) ensureGoogleFont(next.font_family);
    }
    if ("mosaic_font_size_desktop" in data) {
      setDesktopFont(String(data.mosaic_font_size_desktop ?? DEFAULT_DESKTOP));
      setDesktopFontError("");
    }
    if ("mosaic_font_size_mobile" in data) {
      setMobileFont(String(data.mosaic_font_size_mobile ?? DEFAULT_MOBILE));
      setMobileFontError("");
    }
    setHydrated(true);
  }, [data]);

  const handleWordChange = (value) => {
    setWord(String(value ?? "").slice(0, MOSAIC_WORD_MAX));
    markDirty();
  };

  const handleTitleChange = (value) => {
    setTitle(String(value ?? "").slice(0, MOSAIC_TITLE_MAX));
    markDirty();
  };

  const handleRemDraftChange = (setter, errorSetter, value) => {
    const raw = String(value ?? "");
    // Allow empty / partial while typing (e.g. "" or "1.")
    if (raw === "" || PARTIAL_REM_RE.test(raw.replace(/rem$/i, ""))) {
      setter(raw.replace(/rem$/i, ""));
      errorSetter("");
      markDirty();
    }
  };

  const commitRemField = (raw, min, max, fallback, setter, errorSetter, label) => {
    const trimmed = String(raw ?? "").trim();
    if (!trimmed) {
      setter(fallback);
      errorSetter(`${label} cannot be blank.`);
      markDirty();
      return fallback;
    }
    const normalized = toRemString(trimmed, fallback, min, max);
    const num = Number.parseFloat(normalized);
    const outOfRange =
      !Number.isFinite(Number.parseFloat(trimmed)) ||
      Number.parseFloat(trimmed) < min ||
      Number.parseFloat(trimmed) > max;
    setter(normalized);
    errorSetter(
      outOfRange ? `${label} must be between ${min}rem and ${max}rem.` : ""
    );
    markDirty();
    return normalized;
  };

  const commitWordFont = (which) => {
    const isDesktop = which === "desktop";
    const raw = isDesktop ? desktopFont : mobileFont;
    const result = validateWordFontSize(raw, {
      fallback: isDesktop ? DEFAULT_DESKTOP : DEFAULT_MOBILE,
      minRem: WORD_MIN_REM,
      maxRem: isDesktop ? WORD_DESKTOP_MAX_REM : WORD_MOBILE_MAX_REM,
      label: isDesktop ? "Desktop mosaic word size" : "Mobile mosaic word size",
    });
    if (isDesktop) {
      setDesktopFont(result.value);
      setDesktopFontError(result.error);
    } else {
      setMobileFont(result.value);
      setMobileFontError(result.error);
    }
    if (!result.ok) {
      Swal.fire(
        result.exceeded ? "Font size too large" : "Invalid font size",
        result.error,
        "warning"
      );
    }
    markDirty();
    return result;
  };

  const handleSave = async () => {
    if (!currentTheme || !adminToken) {
      Swal.fire("No theme", "Select a theme before saving mosaic settings.", "warning");
      return;
    }

    const trimmedWord = String(word ?? "").trim();
    if (!trimmedWord) {
      Swal.fire("Missing word", "Please enter a mosaic word.", "warning");
      return;
    }
    if (trimmedWord.length > MOSAIC_WORD_MAX) {
      Swal.fire(
        "Too long",
        `Mosaic word can be at most ${MOSAIC_WORD_MAX} characters.`,
        "warning"
      );
      return;
    }

    const trimmedTitle = String(title ?? "").trim().slice(0, MOSAIC_TITLE_MAX);
    if (!trimmedTitle) {
      Swal.fire("Missing title", "Please enter a mosaic title.", "warning");
      return;
    }

    const deskResult = validateWordFontSize(desktopFont, {
      fallback: DEFAULT_DESKTOP,
      minRem: WORD_MIN_REM,
      maxRem: WORD_DESKTOP_MAX_REM,
      label: "Desktop mosaic word size",
    });
    const mobResult = validateWordFontSize(mobileFont, {
      fallback: DEFAULT_MOBILE,
      minRem: WORD_MIN_REM,
      maxRem: WORD_MOBILE_MAX_REM,
      label: "Mobile mosaic word size",
    });

    setDesktopFont(deskResult.value);
    setMobileFont(mobResult.value);
    setDesktopFontError(deskResult.error);
    setMobileFontError(mobResult.error);

    if (!deskResult.ok) {
      Swal.fire(
        deskResult.exceeded ? "Font size too large" : "Invalid font size",
        deskResult.error,
        "warning"
      );
      return;
    }
    if (!mobResult.ok) {
      Swal.fire(
        mobResult.exceeded ? "Font size too large" : "Invalid font size",
        mobResult.error,
        "warning"
      );
      return;
    }

    const desk = deskResult.value;
    const mob = mobResult.value;

    const titleDesk = toRemString(
      titleDesktop,
      DEFAULT_TITLE_DESKTOP,
      TITLE_DESKTOP_MIN,
      TITLE_DESKTOP_MAX
    );
    const titleMob = toRemString(
      titleMobile,
      DEFAULT_TITLE_MOBILE,
      TITLE_MOBILE_MIN,
      TITLE_MOBILE_MAX
    );

    if (!String(titleDesktop).trim() || !String(titleMobile).trim()) {
      setTitleDesktop(titleDesk);
      setTitleMobile(titleMob);
      setTitleDesktopError(
        !String(titleDesktop).trim() ? "Desktop title size cannot be blank." : ""
      );
      setTitleMobileError(
        !String(titleMobile).trim() ? "Mobile title size cannot be blank." : ""
      );
      Swal.fire(
        "Title font size required",
        "Desktop and mobile title sizes cannot be blank. Values were restored to defaults — adjust and save again.",
        "warning"
      );
      return;
    }

    setTitleDesktop(titleDesk);
    setTitleMobile(titleMob);
    setTitleDesktopError("");
    setTitleMobileError("");

    const normalizedTitleStyle = {
      font_family: String(titleFamily || ""),
      font_size_desktop: titleDesk,
      font_size_mobile: titleMob,
      color: titleColor || DEFAULT_TITLE_COLOR,
    };

    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          currentTheme,
          data: {
            mosaic_word: trimmedWord,
            mosaic_title: trimmedTitle,
            mosaic_title_style: normalizedTitleStyle,
            mosaic_font_size_desktop: desk,
            mosaic_font_size_mobile: mob,
          },
        },
        token: adminToken,
      });
      setWord(trimmedWord);
      setTitle(trimmedTitle);
      setTitleDesktop(titleDesk);
      setTitleMobile(titleMob);
      setTitleDesktopError("");
      setTitleMobileError("");
      setDesktopFont(desk);
      setMobileFont(mob);
      setDesktopFontError("");
      setMobileFontError("");
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      clearAdminDirty();
      Swal.fire("Saved", "Mosaic settings updated.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Could not save mosaic settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  const mosaicEnabled =
    String(data?.mosaic_enabled ?? "false").trim().toLowerCase() === "true";

  const previewSize = remPreviewPx(
    toRemString(titleDesktop, DEFAULT_TITLE_DESKTOP, TITLE_DESKTOP_MIN, TITLE_DESKTOP_MAX)
  );

  return (
    <div className="mosaic-admin-page">
      <div className="admin-tab-container mosaic-admin-panel">
        <div className="admin-tab-title">
          <i className="fa-solid fa-th" /> &nbsp; Mosaic settings
        </div>

        {!mosaicEnabled ? (
          <div className="code" style={{ marginBottom: 16 }}>
            Mosaic is currently disabled. Enable it on the Rules page to show “See Mosaic” in the
            gallery.
          </div>
        ) : null}

        <div className="form-group-v1" data-tour="mosaic-title">
          <label htmlFor="mosaic-title">Mosaic title</label>
          <input
            id="mosaic-title"
            type="text"
            maxLength={MOSAIC_TITLE_MAX}
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder={DEFAULT_TITLE}
          />
          <div className="code">
            {String(title).length}/{MOSAIC_TITLE_MAX} characters. Shown above the mosaic on the
            player page.
          </div>
        </div>

        <div className="mosaic-typo-card" data-tour="mosaic-typography">
          <div className="mosaic-typo-head">
            <span className="mosaic-typo-label">Title typography</span>
            <span
              className="mosaic-typo-preview"
              style={{
                fontFamily: titleFamily || undefined,
                fontSize: `${previewSize}px`,
                color: titleColor || DEFAULT_TITLE_COLOR,
              }}
            >
              {title || DEFAULT_TITLE}
            </span>
          </div>

          <div className="mosaic-typo-grid">
            <label className="mosaic-typo-field mosaic-typo-field--family">
              <span>Font family</span>
              <select
                value={titleFamily}
                onChange={(e) => {
                  const v = e.target.value;
                  setTitleFamily(v);
                  if (v) ensureGoogleFont(v);
                  markDirty();
                }}
              >
                {FONT_FAMILY_OPTIONS.map((o) => (
                  <option key={o.label} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="mosaic-typo-field">
              <span>Desktop size</span>
              <div className={`mosaic-rem-input${titleDesktopError ? " has-error" : ""}`}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={String(titleDesktop).replace(/rem$/i, "")}
                  onChange={(e) =>
                    handleRemDraftChange(setTitleDesktop, setTitleDesktopError, e.target.value)
                  }
                  onBlur={() =>
                    commitRemField(
                      titleDesktop,
                      TITLE_DESKTOP_MIN,
                      TITLE_DESKTOP_MAX,
                      DEFAULT_TITLE_DESKTOP,
                      setTitleDesktop,
                      setTitleDesktopError,
                      "Desktop title size"
                    )
                  }
                  placeholder="1.5"
                  aria-label="Title desktop font size in rem"
                />
                <em>rem</em>
              </div>
              <span className={`mosaic-typo-hint${titleDesktopError ? " is-error" : ""}`}>
                {titleDesktopError ||
                  `Min ${TITLE_DESKTOP_MIN} · Max ${TITLE_DESKTOP_MAX} rem`}
              </span>
            </label>

            <label className="mosaic-typo-field">
              <span>Mobile size</span>
              <div className={`mosaic-rem-input${titleMobileError ? " has-error" : ""}`}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={String(titleMobile).replace(/rem$/i, "")}
                  onChange={(e) =>
                    handleRemDraftChange(setTitleMobile, setTitleMobileError, e.target.value)
                  }
                  onBlur={() =>
                    commitRemField(
                      titleMobile,
                      TITLE_MOBILE_MIN,
                      TITLE_MOBILE_MAX,
                      DEFAULT_TITLE_MOBILE,
                      setTitleMobile,
                      setTitleMobileError,
                      "Mobile title size"
                    )
                  }
                  placeholder="1.125"
                  aria-label="Title mobile font size in rem"
                />
                <em>rem</em>
              </div>
              <span className={`mosaic-typo-hint${titleMobileError ? " is-error" : ""}`}>
                {titleMobileError ||
                  `Min ${TITLE_MOBILE_MIN} · Max ${TITLE_MOBILE_MAX} rem`}
              </span>
            </label>

            <label className="mosaic-typo-field mosaic-typo-field--color">
              <span>Color</span>
              <input
                type="color"
                value={titleColor || DEFAULT_TITLE_COLOR}
                onChange={(e) => {
                  setTitleColor(e.target.value);
                  markDirty();
                }}
                aria-label="Title color"
              />
            </label>
          </div>
        </div>

        <div className="form-group-v1" data-tour="mosaic-word">
          <label htmlFor="mosaic-word">Mosaic word</label>
          <input
            id="mosaic-word"
            type="text"
            maxLength={MOSAIC_WORD_MAX}
            value={word}
            onChange={(e) => handleWordChange(e.target.value)}
            placeholder={DEFAULT_WORD}
          />
          <div className="code">
            {String(word).length}/{MOSAIC_WORD_MAX} characters. Max {MOSAIC_WORD_MAX}. Spaces create
            a two-line word.
          </div>
        </div>

        <div className="form-group-v1" data-tour="mosaic-font-desktop">
          <label htmlFor="mosaic-font-desktop">Word font size (desktop)</label>
          <input
            id="mosaic-font-desktop"
            type="text"
            value={desktopFont}
            onChange={(e) => {
              setDesktopFont(e.target.value);
              setDesktopFontError("");
              markDirty();
            }}
            onBlur={() => commitWordFont("desktop")}
            placeholder={DEFAULT_DESKTOP}
            aria-invalid={desktopFontError ? "true" : "false"}
          />
          <div className="code" style={desktopFontError ? { color: "#dc2626" } : undefined}>
            {desktopFontError ||
              `Max ${WORD_DESKTOP_MAX_REM}rem (examples: 16rem, 120px). Caps the auto-fit size on desktop.`}
          </div>
        </div>

        <div className="form-group-v1" data-tour="mosaic-font-mobile">
          <label htmlFor="mosaic-font-mobile">Word font size (mobile)</label>
          <input
            id="mosaic-font-mobile"
            type="text"
            value={mobileFont}
            onChange={(e) => {
              setMobileFont(e.target.value);
              setMobileFontError("");
              markDirty();
            }}
            onBlur={() => commitWordFont("mobile")}
            placeholder={DEFAULT_MOBILE}
            aria-invalid={mobileFontError ? "true" : "false"}
          />
          <div className="code" style={mobileFontError ? { color: "#dc2626" } : undefined}>
            {mobileFontError ||
              `Max ${WORD_MOBILE_MAX_REM}rem (examples: 4rem, 48px). Used below 768px width.`}
          </div>
        </div>

        <div className="mosaic-admin-actions" data-tour="mosaic-save">
          <button
            type="button"
            className="save-and-continue"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save mosaic settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
